import { create } from "zustand";
import { persist } from "zustand/middleware";
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { withActiveTenant } from "../../../../utils/tenantUtils";

const normalizeAmount = (value) => Number(value) || 0;

const buildBudgetCategoryLookup = (budgets = []) => {
  const lookup = {};
  budgets.forEach((budget) => {
    Object.values(budget?.monthlyData || {}).forEach((monthData) => {
      (monthData?.categories || []).forEach((category) => {
        if (category?.id) {
          lookup[category.id] = category.name || category.categoryName || "Uncategorized";
        }
      });
    });
  });
  return lookup;
};

const useReportingStore = create(
  persist(
    (set) => ({
      income: 0,
      expenses: 0,
      savings: 0,
      netBalance: 0,
      goalsProgress: [],
      accountBalances: {},
      transactions: [],
      budgets: [],
      insights: [],
      trends: { income: [], expenses: [] },
      loading: false,
      error: null,

      setIncome: (income) => set({ income }),
      setExpenses: (expenses) => set({ expenses }),
      setSavings: (savings) => set({ savings }),
      setNetBalance: (netBalance) => set({ netBalance }),
      setGoalsProgress: (goalsProgress) => set({ goalsProgress }),
      setAccountBalances: (accountBalances) => set({ accountBalances }),
      setTransactions: (transactions) => set({ transactions }),
      setBudgets: (budgets) => set({ budgets }),
      setInsights: (insights) => set({ insights }),
      setTrends: (trends) => set({ trends }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      fetchDashboardData: async (userId) => {
        set({ loading: true, error: null });
        try {
          await waitForConvexAuthReady();
          // Fetch all data in parallel
          const [transactions, goals, accounts, budgets] = await Promise.all([
            convex.query(api.transactions.listByUser, withActiveTenant()),
            convex.query(api.goals.listGoals, withActiveTenant()),
            convex.query(api.accounts.listAccounts, withActiveTenant()),
            convex.query(api.budgets.listBudgets, withActiveTenant()),
          ]);

          const budgetCategoryLookup = buildBudgetCategoryLookup(budgets);
          const enrichedTransactions = transactions.map((tx) => ({
            ...tx,
            categoryName:
              budgetCategoryLookup[tx.categoryId] ||
              tx.categoryName ||
              tx.category ||
              tx.categoryId ||
              "Uncategorized",
          }));

          set({ transactions: enrichedTransactions });

          const income = enrichedTransactions
            .filter((t) => t.type === "income")
            .reduce((sum, t) => sum + normalizeAmount(t.amount), 0);
          const savings = enrichedTransactions
            .filter((t) => t.type === "savings" || t.type === "saving")
            .reduce((sum, t) => sum + normalizeAmount(t.amount), 0);
          const expenses = enrichedTransactions
            .filter((t) => t.type === "expense")
            .reduce((sum, t) => sum + normalizeAmount(t.amount), 0);
          const netBalance = income - expenses;
          set({ income, expenses, savings, netBalance });

          // Goals progress
          const goalsProgress = goals.map((goal) => {
            const goalId = goal._id || goal.id;
            const cumulativePayments = enrichedTransactions
              .filter((t) => (t.goalId || t.goal) === goalId && (t.type === "savings" || t.type === "saving"))
              .reduce((sum, t) => sum + normalizeAmount(t.amount), 0);
            const current = normalizeAmount(goal.currentAmount) || cumulativePayments;
            const progress = (current / (goal.targetAmount || 1)) * 100;
            return {
              id: goalId,
              name: goal.goalName || goal.name || "Unnamed Goal",
              target: goal.targetAmount,
              current,
              cumulativePayments,
              progress: Math.min(progress, 100),
              expectedCompletion: goal.expectedCompletion,
              status: goal.status || "Unknown",
            };
          });
          set({ goalsProgress });

          // Account balances
          const accountBalances = accounts.reduce((acc, a) => {
            acc[a._id || a.id] = a.balance || 0;
            return acc;
          }, {});
          set({ accountBalances });

          // Budgets with spending
          const enrichedBudgets = budgets.map((b) => {
            const budgetId = b._id || b.id;
            const spent = enrichedTransactions
              .filter((t) => t.budgetId === budgetId && t.type === "expense")
              .reduce((sum, t) => sum + normalizeAmount(t.amount), 0);
            const budgetedAmount = normalizeAmount(b.amount);
            const remaining = budgetedAmount - spent;
            const percentUsed = budgetedAmount > 0 ? (spent / budgetedAmount) * 100 : 0;
            return {
              ...b,
              id: budgetId,
              name: b.metadata?.name || b.name || "Unnamed Budget",
              spent,
              remaining,
              percentUsed,
            };
          });
          set({ budgets: enrichedBudgets });

          // Trends
          const monthlyGroups = enrichedTransactions.reduce((acc, tx) => {
            const month = new Date(tx.date).toLocaleString("default", { month: "short", year: "numeric" });
            if (!acc[month]) acc[month] = { income: 0, expense: 0 };
            if (tx.type === "income") acc[month].income += normalizeAmount(tx.amount);
            if (tx.type === "expense") acc[month].expense += normalizeAmount(tx.amount);
            return acc;
          }, {});
          const trends = Object.entries(monthlyGroups).map(([month, data]) => ({
            month, income: data.income, expense: data.expense,
          }));
          set({ trends });

          // Insights
          const insights = [];
          if (income > expenses)
            insights.push(`You've saved ₵${(income - expenses).toFixed(2)} more than you spent this month 👏.`);
          if (expenses > income)
            insights.push(`Your expenses exceeded income by ₵${(expenses - income).toFixed(2)} — try cutting back.`);
          if (savings > 0)
            insights.push(`You've added ₵${savings.toFixed(2)} to your savings this month.`);
          goalsProgress.forEach((goal) => {
            if (goal.progress >= 100) insights.push(`🎯 Goal "${goal.name}" achieved!`);
            else if (goal.progress > 70) insights.push(`You're ${goal.progress.toFixed(1)}% closer to your "${goal.name}" goal.`);
          });
          set({ insights });
        } catch (err) {
          console.error("Error loading dashboard data:", err);
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },
    }),
    { name: "reporting-store" }
  )
);

export default useReportingStore;
